import axios, { AxiosInstance } from 'axios';
import { z } from 'zod';

import type { ApiConfig } from '@/services/api/configTypes';
import { attachResponseInterceptor } from '@/services/api/interceptors';
import { executeRequest } from '@/services/api/executeRequest';
import type { EndpointBase, EndpointArgsTuple, RuntimeArgs } from '@/services/api/endpointTypes';

export interface ApiInstance {
  endpoint: <const C extends EndpointBase>(
    epConfig: C,
  ) => (...rest: EndpointArgsTuple<C>) => Promise<z.output<C['response']>>;
  configure: (config: ApiConfig) => void;
  getConfig: () => ApiConfig;
  client: AxiosInstance;
}

// Creates a self-contained typed API: its own axios instance, its own lazy
// config slot (filled at boot via configure()), the shared response
// interceptor attached. `label` identifies the instance in the not-configured
// error. Lazy config is required because domain api.ts files statically import
// the endpoint fn at module-load, before boot-time configure() runs.
export function defineApi(label: string): ApiInstance {
  let config: ApiConfig | null = null;
  const getConfig = (): ApiConfig => {
    if (!config) throw new Error(`services/api: "${label}" config not set — configure() was not called at boot`);
    return config;
  };

  const client = axios.create();
  attachResponseInterceptor(client, getConfig);

  const endpoint = <const C extends EndpointBase>(epConfig: C) =>
    (...rest: EndpointArgsTuple<C>) =>
      executeRequest(client, getConfig, epConfig, (rest[0] ?? {}) as RuntimeArgs) as Promise<
        z.output<C['response']>
      >;

  return { endpoint, configure: (c) => { config = c; }, getConfig, client };
}
